/**
 * Builds swatch card markup from catalog records.
 *
 * This is a port of Views/SwatchPage/_SwatchCard.cshtml and _SwatchFavoriteButton.cshtml. The two
 * must stay in step: tools/swatch-render-parity renders every swatch here and diffs the result
 * against the server-rendered card, so a change to either Razor partial that is not made here
 * shows up as a parity failure rather than as a visual bug found later.
 *
 * Output is an HTML string rather than DOM nodes so a whole chunk can be inserted with one
 * innerHTML write. That is the difference between one layout pass per chunk and one per card.
 */

const CART_ICON = '<svg class="swatch-action-icon swatch-cart-icon" width="20" height="20" viewBox="6 6 14 15" fill="none" aria-hidden="true" focusable="false">'
  + '<path fill="currentColor" d="M19.9674 10.9636L18.2228 16.4016C18.0924 16.7889 17.75 17.0471 17.3424 17.0471H10.6087C10.2174 17.0471 9.84239 16.805 9.71196 16.45L7.13587 9.62429H5.65217C5.29348 9.62429 5 9.33384 5 8.97883C5 8.62383 5.29348 8.33337 5.65217 8.33337H7.59239C7.86957 8.33337 8.11413 8.51088 8.21196 8.76906L10.8696 15.7561H17.0652L18.4511 11.3993H10.7717C10.413 11.3993 10.1196 11.1088 10.1196 10.7538C10.1196 10.3988 10.413 10.1084 10.7717 10.1084H19.3478C19.5598 10.1084 19.7554 10.2213 19.8696 10.3827C20 10.5441 20.0326 10.77 19.9674 10.9636ZM10.9348 17.9023C10.6576 17.9023 10.3804 18.0153 10.1848 18.2089C9.98913 18.4025 9.875 18.6769 9.875 18.9512C9.875 19.2255 9.98913 19.4998 10.1848 19.6934C10.3804 19.8871 10.6576 20 10.9348 20C11.212 20 11.4891 19.8871 11.6848 19.6934C11.8804 19.4998 11.9946 19.2255 11.9946 18.9512C11.9946 18.6769 11.8804 18.4025 11.6848 18.2089C11.4891 18.0153 11.212 17.9023 10.9348 17.9023ZM16.788 17.9023C16.5109 17.9023 16.2337 18.0153 16.038 18.2089C15.8424 18.4025 15.7283 18.6769 15.7283 18.9512C15.7283 19.2255 15.8424 19.4998 16.038 19.6934C16.2337 19.8871 16.5109 20 16.788 20C17.0652 20 17.3424 19.8871 17.538 19.6934C17.7337 19.4998 17.8478 19.2255 17.8478 18.9512C17.8478 18.6769 17.7337 18.4025 17.538 18.2089C17.3424 18.0153 17.0652 17.9023 16.788 17.9023Z" />'
  + '<path class="swatch-cart-icon__contents" d="M18.9479 10.1107H8.56396L10.9603 16.6667H16.5516L17.3504 15.7301L18.9479 10.1107Z" fill="transparent" />'
  + '</svg>';

const HEART_PATH = 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';

const heartIcon = fill => '<svg class="swatch-action-icon swatch-favorite-heart" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
  + `<path d="${HEART_PATH}" fill="${fill}" stroke="#ae614c" stroke-width="1.5" />`
  + '</svg>';

/**
 * Escapes for use inside a double-quoted attribute or as text.
 *
 * Razor also encodes "'" as "&#39;", which this does not. That difference disappears once the
 * markup is parsed - both forms produce the same attribute value - so it does not affect the
 * outerHTML comparison the parity tool makes.
 */
const esc = value => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const favoriteForm = (record, assetId) => {
  const id = esc(record.id);
  const code = esc(record.productLineCode);
  const number = esc(record.colorNumber);
  const name = esc(record.colorName);
  const srName = record.colorName ? ` ${name}` : '';
  const isFavorite = assetId != null;

  const action = isFavorite ? 'removefavorite' : 'addfavorite';
  const dataAction = isFavorite ? 'remove' : 'add';

  // The remove button has no align-items-center and the add button does. That is how the Razor
  // partial reads; it is reproduced rather than tidied so render parity stays exact.
  const buttonClass = isFavorite
    ? 'd-flex justify-content-center border border-terracotta bg-transparent p-0 swatch-action-btn swatch-favorite-btn'
    : 'd-flex justify-content-center align-items-center border border-terracotta bg-transparent p-0 swatch-action-btn swatch-favorite-btn';

  const assetInput = isFavorite
    ? `<input type="hidden" name="assetId" value="${esc(assetId)}" />`
    : '';

  const label = isFavorite
    ? `Remove Swatch${srName} From Saved Swatches`
    : `Add Swatch${srName} To Saved Swatches`;

  return [
    `<form class="w-100 swatch-favorite-form" method="post" action="/api/swatchpage/${action}"`,
    ` data-favorite-action="${dataAction}"`,
    ` data-swatch-id="${id}"`,
    ` data-product-line-code="${code}"`,
    ` data-color-number="${number}"`,
    ` data-color-name="${name}">`,
    assetInput,
    `<input type="hidden" name="swatchId" value="${id}" />`,
    `<input type="hidden" name="productLineCode" value="${code}" />`,
    `<input type="hidden" name="colorNumber" value="${number}" />`,
    `<button type="submit" class="${buttonClass}" aria-pressed="${isFavorite}">`,
    '<span class="d-flex flex-row justify-content-start align-items-center p-2">',
    heartIcon(isFavorite ? '#ae614c' : '#ffffff'),
    `<span class="sr-only">${label}</span>`,
    '</span>',
    '</button>',
    '</form>',
  ].join('');
};

/**
 * @param record  a record from swatch-catalog.js
 * @param assetId the digital asset id when this swatch is favorited, otherwise null
 */
const renderSwatchCard = (record, assetId) => {
  const id = esc(record.id);
  const image = esc(record.imageUrl);
  const name = esc(record.colorName);
  const category = esc(record.category);
  const lineName = esc(record.productLineName);
  const code = esc(record.productLineCode);
  const collection = esc(record.collection);

  const number = esc(record.colorNumber);

  return [
    '<div class="swatch-card-col col"',
    ` data-swatch-id="${id}"`,
    ` data-tab="${esc(record.tab)}"`,
    ` data-productline="${esc(record.productLineId)}"`,
    ` data-productline-name="${lineName}"`,
    ` data-category="${category}"`,
    ` data-stylekeys="${esc(record.styleKeys.join('|'))}"`,
    ` data-collection="${collection}"`,
    ` data-colorranges="${esc(record.colorRangeIds.join(','))}"`,
    ` data-solutions="${esc(record.solutionIds.join(','))}"`,
    ` data-color-name="${name}"`,
    ` data-color-number="${number}"`,
    ` data-image-url="${image}"`,
    ` data-product-line-code="${code}"`,
    ` data-visualize-url="${esc(record.visualizeUrl)}">`,
    `<div class="card rounded-0 h-100 ${record.isNew ? 'new-swatch' : ''}">`,
    '<div class="position-relative overflow-hidden swatch-card-media">',
    '<div class="ratio ratio-1x1">',
    '<button type="button" class="d-block w-100 h-100 p-0 border-0 bg-transparent cursor-pointer swatch-view-detail"',
    ` data-swatch-id="${id}" data-image="${image}" aria-label="View details of ${name}">`,
    // loading="lazy" matters as much here as in the Razor partial: cards are appended in chunks
    // ahead of the scroll position, so the ones below the fold must not fetch until reached.
    `<img src="${image}" alt="" class="d-block w-100 h-100 object-fit-cover" loading="lazy" decoding="async" width="300" height="300" />`,
    '</button>',
    '</div>',
    '<div class="swatch-card-hover-actions position-absolute d-flex align-items-center gap-1 bg-white border border-taupe bottom-0 start-0 justify-content-center w-100 p-1">',
    '<div class="swatch-favorite-slot">',
    favoriteForm(record, assetId),
    '</div>',
    '<button type="button" class="d-flex align-items-center border border-terracotta bg-transparent p-0 swatch-action-btn swatch-cart-btn"',
    ' aria-pressed="false"',
    ` data-swatch-id="${id}"`,
    ` data-image-url="${image}"`,
    ` data-name="${name}"`,
    ` data-color-number="${number}"`,
    ` data-collection="${collection}"`,
    ` data-color-name="${name}"`,
    ` data-swatch-category-name="${category}"`,
    ` data-product-line-code="${code}"`,
    ` data-product-line-name="${lineName}">`,
    CART_ICON,
    `<span class="sr-only swatch-cart-label">Add Swatch ${name} To Cart</span>`,
    '</button>',
    '</div>',
    '</div>',
    '</div>',
    '</div>',
  ].join('');
};

export default renderSwatchCard;
export { renderSwatchCard, esc };
